import { Button } from '@newfront-insurance/core-ui/v2';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { type CustomSkillManagementTab } from './custom-skill-model';
import { CustomSkillTabs } from './custom-skill-tabs';
import { getErrorMessage } from '../../lib/error-message';
import { SettingsCreateButton, SettingsPageHeader } from '../settings-page-header';

export function CustomSkillsPage({
  activeTab,
  mySkillCount,
  sharedSkillCount,
  showMySkills,
  showSharedSkills,
  isLoading,
  error,
  children,
  onAddSkill,
  onRetry,
  onTabChange,
}: {
  activeTab: CustomSkillManagementTab;
  mySkillCount: number;
  sharedSkillCount: number;
  showMySkills: boolean;
  showSharedSkills: boolean;
  isLoading: boolean;
  error?: unknown;
  children: ReactNode;
  onAddSkill: () => void;
  onRetry: () => void;
  onTabChange: (tab: CustomSkillManagementTab) => void;
}) {
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-6 bg-white">
      <SettingsPageHeader
        title="Skills"
        description="Reusable prompts you trigger with / in any chat."
        action={
          showMySkills ? (
            <SettingsCreateButton testId="custom-skills-page-new-skill-button" onClick={onAddSkill}>
              New skill
            </SettingsCreateButton>
          ) : null
        }
      />

      <CustomSkillTabs
        activeTab={activeTab}
        mySkillCount={mySkillCount}
        sharedSkillCount={sharedSkillCount}
        showMySkills={showMySkills}
        showSharedSkills={showSharedSkills}
        showWtwSkills
        onChange={onTabChange}
      />

      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-[#546A83]" />
            <span className="ml-2 text-sm text-[#546A83]">Loading custom skills...</span>
          </div>
        ) : error ? (
          <div className="rounded-[4px] border border-[#DEE2E7] bg-white p-4">
            <p className="text-sm text-[#C5342A]">{getErrorMessage(error, 'Unable to load custom skills.')}</p>
            <Button type="button" className="mt-3" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
